// File: frontend/src/api/reports.ts

import axios from 'axios';

// Types
export type StenchLevel = 1 | 2 | 3 | 4 | 5;

export interface StenchReport {
  id: string;
  timestamp: string;
  level: StenchLevel;
  location: string;
  description: string;
  windDirection?: string;
  windSpeed?: string;
  temperature?: string;
  humidity?: string;
  weatherConditions?: string;
  upvotes?: number;
  downvotes?: number;
}

export interface StenchStatistics {
  totalReports: number;
  averageLevel: number;
  worstLocation: string;
  worstLevel: number;
  reportsByLevel: {
    1: number;
    2: number;
    3: number;
    4: number;
    5: number;
  };
  locationStats: Record<string, { count: number; avgLevel: number }>;
}

// API base URL - will be replaced with actual API Gateway URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// API client
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create a new stench report
export const createReport = async (
  level: StenchLevel,
  location: string,
  description: string
): Promise<StenchReport> => {
  const response = await apiClient.post<StenchReport>('/reports', {
    level,
    location,
    description,
  });
  return response.data;
};

// Get stench reports with optional filtering
export const getReports = async (
  startDate?: string,
  endDate?: string,
  location?: string
): Promise<StenchReport[]> => {
  // Build query params
  const params: Record<string, string> = {};
  
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (location) params.location = location;

  const response = await apiClient.get<{ reports: StenchReport[] }>('/reports', { params });
  return response.data.reports;
};

// Vote on a report
export const voteOnReport = async (
  reportId: string,
  voteType: 'upvote' | 'downvote'
): Promise<StenchReport> => {
  const response = await apiClient.post<StenchReport>(`/reports/${reportId}/vote`, {
    voteType,
  });
  return response.data;
};

// Get stench statistics
export const getStatistics = async (
  startDate?: string,
  endDate?: string
): Promise<StenchStatistics> => {
  // Build query params
  const params: Record<string, string> = {};
  
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;

  const response = await apiClient.get<StenchStatistics>('/statistics', { params });
  return response.data;
};
